import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import entries from 'lodash/entries';
import fetch from '@zillow/fetchier';
import _cloneDeep from 'lodash/cloneDeep';
import uuidV4 from 'uuid/v4';
import { parseHiddenJSON } from '@zillow/hidden-block';
import { isSessionStorageSupported } from '@zillow/is-local-storage-supported';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import React from 'react';
import ReactDOM from 'react-dom';
import get from 'lodash/get';
import styled, { ThemeProvider } from 'styled-components';
import { Spacer, ThemeConstellation, ModalDialog, Heading, Paragraph } from '@zillow/constellation';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import PropTypes from 'prop-types';
import AsyncComponent from '@zillow/async-component';
import querystring from 'querystring';

var DEFAULT_TEST_CONFIG = {
  serverURLs: {
    MAX: process.env.LOCAL_MAX || 'https://mortgageapi.qa.zillow.net',
    SAML: process.env.LOCAL_SAML || 'https://saml-authn.TEST_DEFAULT.zillowhomeloans.net',
    SAML_DEV: 'https://saml-authn.TEST_DEV.zillowhomeloans.net',
    SAML_STAGING: 'https://saml-authn.TEST_STAGING.zillowhomeloans.net',
    autocomplete: 'https://static.qa.zillow.net/autocomplete/v3/suggestions'
  },
  lenderWebsiteURLs: {
    base: 'https://www.securecontactpage.com',
    webview: 'https://www.zillow.com'
  },
  userSessionId: '56469a14-c0cf-4133-9ee7-9457befda41e',
  stateAbbreviation: 'WA',
  zipcode: '-1',
  partnerId: 'RD-CZMBMCZ',
  user: {
    id: 'ZUz44hfpyi7vgp_81d4z',
    lastName: 'Corica',
    authenticated: true,
    emailAddress: '2392396@tes.zillow.local',
    logon: '2392396@tes.zillow.local',
    firstName: 'Chris',
    lender: true,
    professional: true
  }
};
function getConfig() {
  var config = parseHiddenJSON('.zmm-api-config');
  var staticConfig = parseHiddenJSON('#zm-api-config');

  if (staticConfig) {
    var KEY = 'zm-userSessionId';
    var userSessionId;

    if (isSessionStorageSupported()) {
      userSessionId = sessionStorage.getItem(KEY) || uuidV4();
      sessionStorage.setItem(KEY, userSessionId);
    } else {
      userSessionId = uuidV4();
    }

    staticConfig.userSessionId = userSessionId;
    config = staticConfig;
  } // Only use test configuration in development or node environments (mocha tests)


  if (!config && process.env.NODE_ENV !== 'production' || process.env.TEST) {
    if (typeof window !== 'undefined' && !process.env.TEST && process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.warn('ZMM API configuration block is missing. Using test values.');
    }

    config = _cloneDeep(DEFAULT_TEST_CONFIG);
  } // _zconfig includes distribution-provided overrides.


  if (typeof window !== 'undefined' && window._zconfig) {
    if (typeof window._zconfig.partnerId === 'string') {
      config.partnerId = window._zconfig.partnerId;
    }

    if (_typeof(window._zconfig.serverURLs) === 'object') {
      config.serverURLs = Object.assign(config.serverURLs, window._zconfig.serverURLs);
    }
  }

  if (!config || !config.serverURLs) {
    config = config || {};
    config.serverURLs = {};
  }

  return config;
}

var inBrowserAndPages = typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.indexOf('/pages') === 0;
var LOGOUT_URL = inBrowserAndPages ? 'https://localhost:8443/Logout.htm' : '/Logout.htm';
var WHOAMI_URL = inBrowserAndPages ? 'https://localhost:8443/user/account/services/WhoAmI.htm' : '/user/account/services/WhoAmI.htm';

var ensureTrailingSlash = function ensureTrailingSlash(url) {
  return url[url.length - 1] === '/' ? url : url + '/';
};

var INVALID_TOKEN_MESSAGE = 'Invalid token';
var LOGIN_REQUIRED_MESSAGE = 'Login required';
var whoAmIMAX = function whoAmIMAX(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      authToken = _ref.authToken;

  if (!authToken) {
    return Promise.resolve();
  }

  var headers = new Headers();
  headers.append('Content-Type', 'text/plain');
  var config = getConfig();
  var MAX_URL = 'unknown';

  if (config && config.serverURLs) {
    MAX_URL = ensureTrailingSlash(config.serverURLs.MAX);
  }

  return fetch(MAX_URL + 'whoAmI', {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      caller: authToken
    })
  }).then(function (res) {
    return res.json();
  }).then(function (json) {
    json.authToken = authToken;
    return json;
  });
};
var rawWhoAmi = function rawWhoAmi() {
  return new Promise(function ($return, $error) {
    var rawResponse, response;
    return Promise.resolve(fetch(WHOAMI_URL, {
      method: 'POST',
      credentials: 'same-origin'
    })).then(function ($await_3) {
      try {
        rawResponse = $await_3;

        var $Try_1_Post = function () {
          try {
            if (response.message === INVALID_TOKEN_MESSAGE) {
              return $error(new Error(INVALID_TOKEN_MESSAGE));
            }

            if (!response.authToken) {
              return $error(new Error(LOGIN_REQUIRED_MESSAGE));
            }

            return $return(response);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        var $Try_1_Catch = function (_unused) {
          try {
            response = {};
            return $Try_1_Post();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        try {
          return Promise.resolve(rawResponse.json()).then(function ($await_4) {
            try {
              response = $await_4;
              return $Try_1_Post();
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }, $Try_1_Catch);
        } catch (_unused) {
          $Try_1_Catch(_unused);
        }
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
};
var netLogout = function netLogout() {
  return fetch(LOGOUT_URL, {
    method: 'GET',
    credentials: 'same-origin'
  });
};
var netWhoAmi = function netWhoAmi() {
  return new Promise(function ($return, $error) {
    var authData;
    return Promise.resolve(rawWhoAmi()).then(function ($await_7) {
      try {
        authData = $await_7;
        return $return(whoAmIMAX(authData));
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
};

var cachedAuthInfo;
var clearAuthInfo = function clearAuthInfo() {
  cachedAuthInfo = undefined;
};

var saveAuthInfo = function saveAuthInfo(info) {
  if (!info || !info.authToken && !info.error) {
    return;
  }

  cachedAuthInfo = info;
  return info;
};

var tokenLogin = function tokenLogin(authToken) {
  return new Promise(function ($return, $error) {
    var info;

    if (!authToken) {
      return $return(Promise.reject(new Error('No auth token provided')));
    }

    return Promise.resolve(whoAmIMAX({
      authToken: authToken
    })).then(function ($await_2) {
      try {
        info = $await_2;
        return $return(saveAuthInfo(info));
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
};
var getAuthInfo = function getAuthInfo(forceNetworkFetch) {
  return new Promise(function ($return, $error) {
    var data;

    if (forceNetworkFetch === void 0) {
      forceNetworkFetch = false;
    }

    if (cachedAuthInfo && !forceNetworkFetch) {
      return $return(cachedAuthInfo);
    }

    var $Try_1_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_1_Catch = function (error) {
      try {
        // We should save the result of this even if it is an error so that
        // we can avoid duplicate calls to WhoAmI.
        saveAuthInfo({
          error: error.message
        });
        return $Try_1_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      return Promise.resolve(netWhoAmi()).then(function ($await_3) {
        try {
          data = $await_3;
          saveAuthInfo(data);
          return $return(data);
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (error) {
      $Try_1_Catch(error);
    }
  });
}; // TODO store mortgage info and auth token separately once npm login component available
var logout = function logout() {
  clearAuthInfo();
  return netLogout();
};

var Login = function Login(_ref) {
  var title = _ref.title,
      props = _objectWithoutProperties(_ref, ["title"]);

  return /*#__PURE__*/React.createElement(AsyncComponent, _extends({
    isNativeApp: true,
    headerText: title
  }, props, {
    onSuccess: function onSuccess(info) {
      return new Promise(function ($return, $error) {
        var _props$onSuccess, _props$onSuccess2, _await$rawWhoAmi, authToken;

        if (info !== null && info !== void 0 && info.authToken) {
          props === null || props === void 0 ? void 0 : (_props$onSuccess = props.onSuccess) === null || _props$onSuccess === void 0 ? void 0 : _props$onSuccess.call(props, info.authToken);
          return $If_1.call(this);
        } else {
          if (!info || info.message === 'account created') {
            return Promise.resolve(rawWhoAmi()).then(function ($await_3) {
              try {
                _await$rawWhoAmi = $await_3, authToken = _await$rawWhoAmi.authToken;
                props === null || props === void 0 ? void 0 : (_props$onSuccess2 = props.onSuccess) === null || _props$onSuccess2 === void 0 ? void 0 : _props$onSuccess2.call(props, authToken);
                return $If_2.call(this);
              } catch ($boundEx) {
                return $error($boundEx);
              }
            }.bind(this), $error);
          }

          function $If_2() {
            return $If_1.call(this);
          }

          return $If_2.call(this);
        }

        function $If_1() {
          return $return();
        }
      });
    },
    loader: function loader() {
      return import(
      /* webpackChunkName: "reg-login" */
      '@zillow/reg-login');
    }
  }));
};
Login.propTypes = process.env.NODE_ENV !== "production" ? {
  title: PropTypes.string,
  onSuccess: PropTypes.func
} : {};

var createRenderNode = function createRenderNode(nodeId, parent) {
  var node = document.createElement('div');
  node.id = nodeId;
  parent.appendChild(node);
  return node;
};

var getRenderNode = function getRenderNode(nodeId, parentNodeId) {
  if (typeof document === 'undefined') {
    return null;
  }

  var node = document.getElementById(nodeId);

  if (!node) {
    var _ref;

    var parent = (_ref = parentNodeId && document.getElementById(parentNodeId)) !== null && _ref !== void 0 ? _ref : document.body;
    return createRenderNode(nodeId, parent);
  }

  return node;
};

var _templateObject;
var Wrapper = styled(Spacer)(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n    overflow: auto; // required for mobile screen since the social icons are bleading outside\n"])));
/**
 * Return a promise to log the user in that will either
 * - resolve with the auth token of their new sessions
 * - reject
 *
 * Only one lightbox will be open at a time. Any login requests while that lightbox is open
 * will share the same Promise
 */

var activeLightboxPromise;
var requestLoginLightbox = function requestLoginLightbox() {
  if (activeLightboxPromise) {
    return activeLightboxPromise;
  }

  var renderNode = getRenderNode('zm-auth-lightbox');

  if (!renderNode) {
    return Promise.reject();
  }

  var unmount = function unmount() {
    ReactDOM.unmountComponentAtNode(renderNode);
    activeLightboxPromise = undefined;
  };

  activeLightboxPromise = new Promise(function (resolve, reject) {
    var handleClose = function handleClose() {
      unmount();
      reject();
    };

    var handleSuccess = function handleSuccess(token) {
      unmount();
      resolve(token);
    };

    var handleFailure = function handleFailure(err) {
      // If the user enters the wrong password, leave the lightbox up so they can try again
      var hasBadCredentialsError = !!get(err, 'response.body.validationErrors.badCredentials');

      if (!hasBadCredentialsError) {
        handleClose();
      }
    };

    ReactDOM.render( /*#__PURE__*/React.createElement(ThemeProvider, {
      theme: ThemeConstellation
    }, /*#__PURE__*/React.createElement(ModalDialog, {
      isOpen: true,
      onClose: handleClose
    }, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Login, {
      onSuccess: handleSuccess,
      onFailure: handleFailure
    })))), renderNode);
  });
  return activeLightboxPromise;
};

var PERIMETER_X_CAPTCHA_NODE_ID = 'px-captcha'; // the container for which perimeterX will render the captcha challenge

var CaptchaChallengeContainer = function CaptchaChallengeContainer() {
  return /*#__PURE__*/React.createElement("div", {
    id: PERIMETER_X_CAPTCHA_NODE_ID
  });
};

var Captcha = function Captcha() {
  return /*#__PURE__*/React.createElement(Spacer, {
    margin: "md"
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    as: "h3"
  }, "Security check"), /*#__PURE__*/React.createElement(Paragraph, {
    marginTop: "sm"
  }, "Check the box below to proceed."), /*#__PURE__*/React.createElement(CaptchaChallengeContainer, null));
};

var FORCE_PX_BLOCK_HEADER = {
  KEY: 'x-px-block',
  VALUE: 1
}; // force a perimeterX block on requests, e.g. ?forcePXBlock=submitRequest for a single request
// path and ?forcePXBlock=submitRequest&forcePXBlock=getLoanQuotes for multiple request paths

var shouldForcePerimeterXBlock = function shouldForcePerimeterXBlock(requestPath) {
  var _querystring$parse = querystring.parse(document.location.search.substring(1)),
      _querystring$parse$fo = _querystring$parse.forcePXBlock,
      forcePXBlock = _querystring$parse$fo === void 0 ? [] : _querystring$parse$fo;

  return forcePXBlock.includes(requestPath);
};
var isPerimeterXBlockResponse = function isPerimeterXBlockResponse(response) {
  return !!(response !== null && response !== void 0 && response.blockScript);
};
var PERIMETER_X_PROPERTIES_TO_ASSIGN = ['appId', 'jsClientSrc', 'firstPartyEnabled', 'uuid', 'hostUrl', 'blockScript', 'onCaptchaSuccess'];

var uppercaseFirstLetter = function uppercaseFirstLetter(input) {
  return "" + input[0].toUpperCase() + input.slice(1);
}; // assign properties from perimeterX response to window e.g. assign hostUrl to window._pxHostUrl


var assignPerimeterXPropertiesToWindow = function assignPerimeterXPropertiesToWindow(perimeterXResponse) {
  Object.keys(perimeterXResponse).filter(function (key) {
    return PERIMETER_X_PROPERTIES_TO_ASSIGN.includes(key);
  }).forEach(function (key) {
    var perimeterXWindowKey = "_px" + uppercaseFirstLetter(key);
    window[perimeterXWindowKey] = perimeterXResponse[key];
  });
};

var createPerimeterXBlockScript = function createPerimeterXBlockScript(blockScriptSrc) {
  var script = document.createElement('script');
  script.src = blockScriptSrc;
  document.head.appendChild(script);
};
/**
 * Return a promise to have the user complete a captcha.
 *
 * Only one lightbox will be open at a time. Any captcha requests while that lightbox is open
 * will share the same Promise.
 */


var activeLightboxPromise$1;
var requestCaptchaLightbox = function requestCaptchaLightbox(perimeterXResponse) {
  if (activeLightboxPromise$1) {
    return activeLightboxPromise$1;
  }

  var renderNode = getRenderNode('perimeter-x-lightbox', 'zmm-distribution');

  if (!renderNode) {
    return Promise.reject();
  }

  var unmount = function unmount() {
    ReactDOM.unmountComponentAtNode(renderNode);
    activeLightboxPromise$1 = undefined;
  };

  activeLightboxPromise$1 = new Promise(function (resolve, reject) {
    var handleClose = function handleClose() {
      unmount();
      reject();
    };

    var handleSuccess = function handleSuccess() {
      unmount();
      resolve();
    };

    assignPerimeterXPropertiesToWindow(Object.assign({}, perimeterXResponse, {
      onCaptchaSuccess: handleSuccess
    }));
    createPerimeterXBlockScript(perimeterXResponse === null || perimeterXResponse === void 0 ? void 0 : perimeterXResponse.blockScript);
    ReactDOM.render( /*#__PURE__*/React.createElement(ThemeProvider, {
      theme: ThemeConstellation
    }, /*#__PURE__*/React.createElement(ModalDialog, {
      isOpen: true,
      onClose: handleClose
    }, /*#__PURE__*/React.createElement(Captcha, null))), renderNode);
  });
  return activeLightboxPromise$1;
};

/*
 This module provides functionality for calling APIs on the mortgage API server.
*/

var DEFAULT_TIMEOUT = 10000;
var MAX_URL_LENGTH = 2047; // see http://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers

var MISSING_USER_SESSION_ID = 'deadbeef-dead-beef-dead-beefdeadbeef';
/*
 * Construct query parameters for an API server.
 *
 * ###### Numbers, Strings, Booleans
 *
 *     apiServerQueryParams({ myNumber: 1, myString: 'abc', myBoolean: true }); // "?myNumber=1&myString=abc&myBoolean=true"
 *
 * ###### Arrays
 *
 *     apiServerQueryParams({ myArray: [ 'a', 'b', 'c' ] }); // "?myArray.0=a&myArray.1=b&myArray.2=c"
 *
 * ###### Objects
 *
 *     apiServerQueryParams({ myObject: { a: 1, b: 2: c: 3 }); // "?myObject.a=1&myObject.b=2&myObject.c=3"
 *
 * ###### Empty
 *
 *     apiServerQueryParams({ myObject: {}, myArray: [], myString: '' }); // "?myObject=&myArray=&myString="
 */

function apiServerQueryParams(params) {
  var arr = [];

  function recurse(v, k) {
    if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
      // Primitives
      arr.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    } else if (_typeof(v) === 'object' && v !== null && (Object.keys(v).length > 0 || v.length)) {
      // Non-empty objects/arrays
      Object.keys(v).forEach(function (kk) {
        recurse(v[kk], k + '.' + kk);
      });
    } else if (typeof v !== 'undefined') {
      // Empty objects/arrays, null
      arr.push(encodeURIComponent(k) + '=');
    }
  } // Recursively iterate over params


  Object.keys(params).forEach(function (k) {
    recurse(params[k], k);
  });
  return arr.length ? '?' + arr.join('&') : '';
}

var APIService = /*#__PURE__*/function () {
  function APIService(baseURL) {
    this.baseURL = baseURL;
  }

  var _proto = APIService.prototype;

  _proto.url = function url(params) {
    return this.baseURL + '/' + params.action + apiServerQueryParams(params.input || {});
  };

  _proto.GET = function GET(givenParams) {
    return this.promise(Object.assign({}, givenParams, {
      post: false
    }));
  };

  _proto.POST = function POST(givenParams) {
    return this.promise(Object.assign({}, givenParams, {
      post: true
    }));
  }
  /**
   * API.<serverName>.promise(givenParams)
   *
   * @param givenParams
   * @param givenParams.action {String}
   * @param givenParams.input {Object} [optional] - An object containing the inputs to the action. The contents of this object depend on the action's specification.
   * @param givenParams.includeAuth {Boolean} [optional]
   * @param givenParams.post {Boolean} [optional]
   * @param givenParams.timeout - in milliseconds, default 10000
   * @param givenParams.authentication
   * @param givenParams.perimeterXEnabled {Boolean} [optional] - enable perimeter X support for the API
   * @returns {Promise}
   */
  ;

  _proto.promise = function promise(givenParams) {
    var _this = this;

    var cancelAPIRequest;
    var cancelPromise = new Promise(function (resolve, reject) {
      cancelAPIRequest = function cancelAPIRequest() {
        reject({
          cancelled: true
        });
      };
    });
    var requestPromise = new Promise(function (resolve, reject) {
      var authRequested = !!givenParams.includeAuth;
      var timeout = givenParams.timeout || DEFAULT_TIMEOUT;
      var authentication = givenParams.authentication;
      var usePOST = givenParams.post;

      if (usePOST === undefined) {
        usePOST = true;
      } // Clone incoming input for potential modification later


      if (givenParams.input) {
        givenParams.input = Object.assign({}, givenParams.input);
      }

      var authPromise = Promise.resolve();

      if (authRequested) {
        authPromise = getAuthInfo();
      }

      function handleResponse(response, definitelyFailed, retry, authRetried) {
        if (response && !response.error && !definitelyFailed) {
          resolve(response);
        } else if (response && response.error === 'LoginRequired' && usePOST) {
          if (!authRequested) {
            // eslint-disable-next-line no-console
            console.warn('An API call returned LoginRequired, but you did not request auth. You probably wanted to set includeAuth: true in this call.');
          }

          if (!authRetried) {
            var _givenParams$input;

            // eslint-disable-next-line no-console
            console.info('zmm-api', "Refreshing login info to avoid LoginRequired failure on request to " + givenParams.action, "old token " + ((_givenParams$input = givenParams.input) === null || _givenParams$input === void 0 ? void 0 : _givenParams$input.caller));
            return getAuthInfo(true).then(function (_ref) {
              var authToken = _ref.authToken;
              givenParams.input = givenParams.input || {};
              givenParams.input.caller = authToken;
            }).then(function () {
              return retry(givenParams, true);
            }).catch(function () {
              return handleResponse(response, definitelyFailed, retry, true);
            });
          }

          if (authentication !== 'error') {
            // eslint-disable-next-line no-console
            console.info('zmm-api', "Attempting login request to retry " + givenParams.action);
            return requestLoginLightbox().then(function (token) {
              givenParams.input = givenParams.input || {};
              givenParams.input.caller = token;
              return tokenLogin(token).then(handlePermissionsUpdate);
            }).then(function () {
              return retry(givenParams, authRetried);
            }).catch(function () {
              return reject({
                error: 'LoginRequired'
              });
            });
          }

          reject(response || {
            error: 'ConnectionFailure'
          });
        } else if (isPerimeterXBlockResponse(response)) {
          requestCaptchaLightbox(response).then(function () {
            retry(givenParams, authRetried);
          });
        } else {
          reject(response || {
            error: 'ConnectionFailure'
          });
        }
      }

      var go = function go(params, authRetried) {
        var method;
        var url; // Build the URL and, if doing a POST, the headers and payload as well.

        if (!usePOST) {
          method = 'GET';
          url = _this.url(params); // If the GET URL is really long, switch to POST to avoid issues
          // with browser or server URL length limits.

          if (url.length > MAX_URL_LENGTH) {
            usePOST = true; // will cause url will be recomputed
          }
        }

        if (usePOST) {
          method = 'POST';
          url = _this.baseURL + '/' + params.action; // When POSTing, include partnerId in query string
          // to help with call identification in logs

          if (params.input && params.input.partnerId) {
            params.queryStringPartnerId = params.input.partnerId;
            delete params.input.partnerId;
          }
        }

        var fetchOpts = {
          method: method,
          timeout: timeout,
          headers: {
            Accept: 'application/json'
          }
        };

        if (params.perimeterXEnabled) {
          // PerimeterX utilizes cookies set by the sensor code hosted on www.(streeteasy|trulia|zillow).com
          // In order for these to be passed to sub-domains (e.g. mortgageapi.zillow.com), the "withCredentials"
          // option must be set in order for the browser to pass cross-domain cookies.
          // See https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
          fetchOpts.credentials = 'include';

          if (shouldForcePerimeterXBlock(params.action)) {
            fetchOpts.headers[FORCE_PX_BLOCK_HEADER.KEY] = FORCE_PX_BLOCK_HEADER.VALUE;
          }
        }

        if (usePOST) {
          // "Simple" CORS requires POST Content-Type be one of:
          //   - application/x-www-form-urlencoded
          //   - multipart/form-data
          //   - text/plain
          // We use text/plain as it is the "least wrong" of the three.
          // Our API servers ignore the Content-Type header, so this
          // doesn't cause any issues.
          fetchOpts.headers['Content-Type'] = 'text/plain';
          fetchOpts.body = params.input || {};
        }

        if (params.queryStringPartnerId) {
          url += "?partnerId=" + params.queryStringPartnerId;
        }

        if (params.input && params.input.caller && typeof params.input.caller !== 'string') {
          // eslint-disable-next-line no-console
          console.warn(new Error("Non-string caller for " + params.action + ": " + JSON.stringify(params.input.caller)));
          return handleResponse({
            error: 'LoginRequired'
          }, true, go, authRetried);
        }

        fetch(url, fetchOpts).then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) {
              handleResponse(json, true, go, authRetried);
            } else {
              handleResponse(json, false, go, authRetried);
            }
          });
        }).catch(function () {
          // Failed to fetch
          handleResponse(null, true, go, authRetried);
        });
      };

      return authPromise.then(function (info) {
        if (authRequested) {
          givenParams.input = givenParams.input || {};

          if (info && info.authToken) {
            givenParams.input.caller = info.authToken;
          } else {
            // Unable to get auth info, probably due to logout elsewhere.
            // Short-circuiting to an assumed LoginRequired response
            return handleResponse({
              error: 'LoginRequired'
            }, true, go);
          }
        }

        go(givenParams);
      }, function () {
        return (// Something went wrong during getting auth info
          // Short-circuiting to an assumed LoginRequired response
          handleResponse({
            error: 'LoginRequired'
          }, true, go)
        );
      });
    });
    var p = Promise.race([cancelPromise, requestPromise]);
    p.cancelAPIRequest = cancelAPIRequest;
    return p;
  };

  return APIService;
}();

var API = {};
var config = getConfig();
function defineAPIService(id, url) {
  API[id] = new APIService(url);
  config.serverURLs[id] = url;
}
function createMAX(url) {
  defineAPIService('MAX', url);
}

if (config) {
  // ZM-3137: some mobile browsers are passing null for the session ID.
  // In that case, fall back to a fake session ID we can easily find in logs, db, etc.
  if (config.userSessionId === null || config.userSessionId === 'null') {
    config.userSessionId = MISSING_USER_SESSION_ID;
  } // Copy select properties from the configuration.

  /*
   The partner ID that clients should use when a partnerId parameter
   is required by an API.
   */


  API.partnerId = config.partnerId;
  /*
   The user session ID that clients should use when a userSessionId parameter
   is required by an API.
   */

  API.userSessionId = config.userSessionId;
  /*
   The state abbreviation of the state where the browser is probably located.
   */

  API.stateAbbreviation = config.stateAbbreviation;
  /*
   If the state abbreviation lookup above failed and returned the default (MO)
   */

  API.stateAbbreviationLookupFailed = config.stateAbbreviationLookupFailed;
  /*
   The zip code where the browser is probably located, or -1 if unable to locate.
   */

  API.zipcode = config.zipcode === '00000' ? '-1' : config.zipcode; // TODO '00000' check is for PRE dom-turd only. Can remove post-AWS.

  /*
   Information about the logged-in user. This property will only be present if the
   user is logged in to the site. If the logged-in user is impersonating another
   user, the information will be that of the user being impersonated.
    user.id (string) The user's ID.
   user.logon (string) The user's logon.
   user.authenticated (boolean) Whether the user is authenticated. If this property is false, the user is recognized but not authenticated.
   user.lender (boolean) Whether the user is a lender.
   user.professional (boolean) Whether the user is a professional.
   user.admin (boolean) Whether the user has administrator privileges. DEPRECATED in favor of 'permissions'.
   user.firstName (string) The user's first name, or undefined if the user has not provided this information.
   user.lastName (string) The user's last name, or undefined if the user has not provided this information.
   user.emailAddress (string) The user's email address.
    API.user will be populated in `handlePermissionsUpdate` below asynchronously
   */

  API.preUserId = config.user !== undefined ? config.user.id : undefined;
  /*
   The bot identifier for the bot, if any, that loaded the page.
   If the page was not loaded by a bot, this property's value will be undefined.
   */

  if (config.bot) {
    API.bot = config.bot;
  }
  /*
   API services.
    Pass an object containing the following keys to API.<serverName>.GET() or API.<serverName>.POST(),
   where `serverName` is a key from `config.serverURLs`:
    action: (string) The name of the action to call.
   includeAuth: (optional boolean) Whether to include the caller's authentication token.
   input: (optional object) An object containing the inputs to the action. The contents of this object depend on the action's specification.
   timeout: (optional number) The timeout, in milliseconds. If not specified, a default timeout will be used.
   authentication: (optional string) How to handle un-authenticated users. One of the following
      - 'lightbox': default - opens the login lightbox if user is not authenticated
      - 'error': passes the error down to reject function
   */
  // initialize an `APIService` for each url in config, including `MAX`.


  entries(config.serverURLs).forEach(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        server = _ref3[0],
        url = _ref3[1];

    return defineAPIService(server, url);
  });
} else {
  API.MAX = {
    promise: function promise() {
      return Promise.reject(new Error('Unable to call MAX'));
    },
    GET: function GET() {
      return Promise.reject(new Error('Unable to call MAX'));
    },
    POST: function POST() {
      return Promise.reject(new Error('Unable to call MAX'));
    }
  };
}

function handlePermissionsUpdate(res) {
  if (res !== null && res !== void 0 && res.user) {
    var hasProfileType = function hasProfileType(type) {
      if (res.user.profileTypes) {
        if (res.user.profileTypes.indexOf(type) > 0) {
          return true;
        }
      }

      return false;
    };

    var isLender = hasProfileType('Lender');
    API.user = Object.assign({}, API.user, res.user, {
      hasProfileType: hasProfileType,
      // type => bool
      // Backwards compat for isLender, lender bools (Deprecated, use profileType instead)
      isLender: isLender,
      lender: isLender,
      emailAddress: res.user.logon
    });
    return res.user.permissions;
  }
}

function updateHDPCalcMarketing(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      propertyAddress = _ref.propertyAddress,
      propertyImageURL = _ref.propertyImageURL,
      propertyURL = _ref.propertyURL;

  if (API.preUserId && propertyAddress && propertyImageURL && propertyURL) {
    return API.MAX.POST({
      action: 'updateHDPCalcMarketing',
      input: {
        userId: API.preUserId,
        propertyAddress: propertyAddress,
        propertyImageURL: propertyImageURL,
        propertyURL: propertyURL
      }
    });
  }
}

export default API;
export { INVALID_TOKEN_MESSAGE, LOGIN_REQUIRED_MESSAGE, Login, clearAuthInfo, createMAX, getAuthInfo, getConfig, handlePermissionsUpdate, logout, requestLoginLightbox, tokenLogin, updateHDPCalcMarketing as updateHdpCalcMarketingForUser };
